/*
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
* Distributed under the TruVideo proprietary license.
*/
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import ReviewShell from './ReviewShell';

// Shown after Save & Exit uploads whatever was pending — a read-only review
// of everything captured so far. Tapping Save hands off to onFinish (which
// closes the session and moves on to a Done-style confirmation screen).
const SavedExit = ({ items, capturedFiles, serverCompleted, completedItemsMedia, assignment, workflow, branding, editableItemIds, onRetake, onDelete, onDeletePhoto, onAddPhoto, onOpenNotes, notes, onAddNote, onSave, onFinish, token, pendingSyncCount = 0, syncStatus, onDismissSyncStatus }) => {
  const [finishing, setFinishing] = useState(false);
  const [saveError, setSaveError] = useState(null);
  // Blocks Save while other captures are still queued/mid-sync — otherwise
  // the session could be released (and this device's local queue deleted,
  // see fetchCaptureData's offlineDb.deleteDatabase) while an item hasn't
  // actually made it to the server yet.
  const [syncBlockedNotice, setSyncBlockedNotice] = useState(false);

  useEffect(() => {
    if (pendingSyncCount === 0) setSyncBlockedNotice(false);
  }, [pendingSyncCount]);

  // Releases the session and hands off to the Done-style screen. onSave
  // already swallows its own errors (see handleConfirmSaveAndExit in
  // Capture.jsx), so this always proceeds to onFinish.
  const handleSaveClick = async () => {
    if (finishing) return;
    if (pendingSyncCount > 0) {
      setSyncBlockedNotice(true);
      return;
    }
    setFinishing(true);
    setSaveError(null);
    try {
      await onSave();
      onFinish();
    } catch (err) {
      console.error('Failed to save and exit:', err);
      setSaveError('Something went wrong while saving your progress. Please try again.');
      setFinishing(false);
    }
  };

  return (
    <ReviewShell
      items={items}
      capturedFiles={capturedFiles}
      serverCompleted={serverCompleted}
      completedItemsMedia={completedItemsMedia}
      assignment={assignment}
      workflow={workflow}
      branding={branding}
      heading="Check your media before submitting"
      body="Review each item below. You can retake or remove any capture before submitting your media."
      error={saveError}
      onNotesClick={onOpenNotes}
      notes={notes}
      onAddNote={onAddNote}
      actionLabel={finishing ? 'Saving…' : 'Save'}
      onAction={handleSaveClick}
      actionDisabled={finishing}
      editableItemIds={editableItemIds}
      onRetake={onRetake}
      onDelete={onDelete}
      onDeletePhoto={onDeletePhoto}
      onAddPhoto={onAddPhoto}
      token={token}
      syncBlockedNotice={syncBlockedNotice}
      onDismissSyncBlockedNotice={() => setSyncBlockedNotice(false)}
      syncStatus={syncStatus}
      onDismissSyncStatus={onDismissSyncStatus}
    />
  );
};

export default SavedExit;

SavedExit.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      assignmentItemId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      type: PropTypes.string,
      description: PropTypes.string,
      required: PropTypes.bool,
      status: PropTypes.string,
    })
  ),
  capturedFiles: PropTypes.object,
  serverCompleted: PropTypes.object,
  completedItemsMedia: PropTypes.object,
  assignment: PropTypes.shape({
    expiresAt: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    submitterName: PropTypes.string,
    workflowName: PropTypes.string,
    customerReferenceNumber: PropTypes.string,
  }),
  workflow: PropTypes.shape({
    branding: PropTypes.shape({
      logoUrl: PropTypes.string,
    }),
  }),
  branding: PropTypes.shape({
    theme: PropTypes.string,
  }),
  editableItemIds: PropTypes.instanceOf(Set),
  onRetake: PropTypes.func,
  onDelete: PropTypes.func,
  onDeletePhoto: PropTypes.func,
  onAddPhoto: PropTypes.func,
  onOpenNotes: PropTypes.func,
  notes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      note: PropTypes.string,
      timestamp: PropTypes.string,
    })
  ),
  onAddNote: PropTypes.func,
  onSave: PropTypes.func.isRequired,
  onFinish: PropTypes.func.isRequired,
  token: PropTypes.string,
  pendingSyncCount: PropTypes.number,
  syncStatus: PropTypes.shape({
    status: PropTypes.oneOf(['success', 'error']),
    message: PropTypes.string,
  }),
  onDismissSyncStatus: PropTypes.func,
};
